import React, {FC, useCallback} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {CardGroupModel} from '../../../models/api/cardGroupModel';
import {CardModel} from '../../../models/api/cardModel';
import {CreateCardGroupParams} from '../../../store/data/cardGroups/cardGroupsActions';
import {CardGroupPopover, OnConfirmOptions} from './cardGroupPopover';

/*
 * Props.
 */

interface CreateNewGroupPopoverProps {
  popoverProps: PopoverTransferProps;
  card?: CardModel;
  userNamespace: string;
  onCreateClose: (arg1: boolean, model?: CardGroupModel) => void;
  createCardGroupAsync: (params: CreateCardGroupParams) => Promise<CardGroupModel>;
  addCardToGroupAsync: (group: CardGroupModel, cardId: number) => Promise<void>;
}

/*
 * Component.
 */

export const CreateNewGroupPopover: FC<CreateNewGroupPopoverProps> = ({
  popoverProps,
  card,
  userNamespace,
  onCreateClose,
  createCardGroupAsync,
  addCardToGroupAsync,
}) => {
  const isPrivateCard = card?.namespace ? isTeammateNamespace(card.namespace) : false;

  const onCreate: (options: OnConfirmOptions) => Promise<void> = useCallback(
    async ({name, namespace}) => {
      const cardGroup = await createCardGroupAsync({name, namespace});
      if (card) {
        await addCardToGroupAsync(cardGroup, card.id);
      }

      onCreateClose(true, cardGroup);
    },
    [card, createCardGroupAsync, addCardToGroupAsync, onCreateClose],
  );

  return (
    <CardGroupPopover
      popoverProps={popoverProps}
      userNamespace={userNamespace}
      isNamespaceDropdownDisabled={isPrivateCard}
      defaultNamespace={card?.namespace}
      onClose={onCreateClose}
      onConfirm={onCreate}
      namespaceDisplayMessage={renderNamespaceDisplayMessage()}
    />
  );
};

/*
 * Helpers.
 */

function renderNamespaceDisplayMessage() {
  return (
    <Trans id="contact-group-dropdown-group-access-message">
      Private contacts can only be added to private lists
    </Trans>
  );
}
